import { Box, Paper, Stack, Typography, useTheme } from '@mui/material'
import { useEffect, useState } from 'react'
import { Outlet, useNavigate, useSearchParams } from 'react-router-dom'

import LoadingWithBackdrop from '~/components/loadingWithBackdrop'
import { EChatStatus } from '~/features/agents/models'
import {
  CALENDLY_PORTAL_ID,
  EMBEDDED_CHAT_KEY,
  EMBEDDED_CHAT_VALUE,
} from '~/features/chatbot/constants'
import usePopulateChatbot from '~/features/chatbot/hooks/usePopulateChatbot'
import { EChatbotTheme } from '~/features/settings/models'
import useDismissAllToasts from '~/hooks/useDismissAllToasts'
import Footer from '~/layouts/chatbot/footer'
import { getLayoutColors } from '~/layouts/chatbot/utils'
import PageLayout from '~/layouts/page'
import { EMarketingClientPath } from '~/models/api'
import { store } from '~/store'

function ChatBotLayout() {
  useDismissAllToasts()
  const [showLoading, setShowLoading] = useState(true)
  const navigate = useNavigate()
  const chatbot = store((state) => state.chatbot)
  const theme = useTheme()
  const { isError, isLoading } = usePopulateChatbot()
  const [searchParams] = useSearchParams()
  const isEmbedded = searchParams.get(EMBEDDED_CHAT_KEY) === EMBEDDED_CHAT_VALUE

  const { wrapperBackgroundColor, containerBackgroundColor } = getLayoutColors(
    chatbot.layout,
    theme,
  )
  // bypass footRef weird behavior ( needed an extra render to get the correct height )
  useEffect(() => {
    if (!isLoading && !isError && showLoading) {
      setShowLoading(false)
    }
  }, [isLoading, isError, showLoading])

  // check chatStatus availability
  useEffect(() => {
    if (
      !isLoading &&
      !isError &&
      chatbot.agent.chatStatus !== EChatStatus.active &&
      !isEmbedded
    ) {
      navigate(EMarketingClientPath.home)
    }
  }, [isLoading, isError, chatbot.agent.chatStatus, navigate, isEmbedded])

  if (isLoading || (isError && !isEmbedded)) {
    return <LoadingWithBackdrop show />
  }

  if (chatbot.agent.chatStatus !== EChatStatus.active && isEmbedded) {
    return (
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          padding: 3,
        }}
      >
        <Typography variant="h6" sx={{ textAlign: 'center' }}>
          Provided agent ID is inactive or doesn&apos;t exist.
        </Typography>
      </Stack>
    )
  }

  return (
    <>
      <PageLayout
        sx={{
          minHeight: '100svh',
          backgroundColor: wrapperBackgroundColor,
        }}
        containerProps={{
          sx: {
            minHeight: '100svh',
            maxWidth: { xxxs: '100%', md: 800 },
            minWidth: 300,
            paddingInline: { xxxs: 0 },
            overflow: 'hidden',
          },
        }}
      >
        <Paper
          elevation={
            chatbot.layout.selection.theme === EChatbotTheme.linkView ? 0 : 3
          }
          sx={{
            backgroundColor: containerBackgroundColor,
            height: '100vh',
            overflow: 'hidden',
          }}
        >
          <Stack
            spacing={0}
            sx={{
              height: '100%',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'flex-start',
                flex: 1,
                overflow: 'hidden',
              }}
            >
              <Outlet />
            </Box>
            {chatbot.agent.showBranding && <Footer />}
          </Stack>
        </Paper>
      </PageLayout>

      <Box
        id={CALENDLY_PORTAL_ID}
        sx={{
          '& .calendly-overlay .calendly-popup': {
            maxHeight: '100vh',
            minWidth: '100%',
          },
        }}
      />
    </>
  )
}

export default ChatBotLayout
